import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, tokens } from '../lib/api';
import type { AuthResult, UserView } from '../lib/types';

interface AuthCtx {
  user: UserView | null;
  loading: boolean;
  requestEmailOtp: (email: string) => Promise<void>;
  verifyEmailOtp: (email: string, code: string) => Promise<void>;
  loginGoogle: (idToken: string) => Promise<void>;
  logout: () => void;
}
const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserView | null>(null);
  const [loading, setLoading] = useState(true);

  // restore session
  useEffect(() => {
    if (!tokens.access()) { setLoading(false); return; }
    api.get<UserView>('/api/auth/me')
      .then(setUser)
      .catch((e) => {
        // Only discard the session for a genuine AUTH failure. Clearing on ANY error meant a
        // transient network blip — or a 404 from a valid-but-unusual token, which is exactly what
        // impersonation produced — silently logged the user out and bounced them to /login.
        const status = (e as { status?: number })?.status;
        if (status === 401 || status === 403) tokens.clear();
      })
      .finally(() => setLoading(false));
  }, []);

  const requestEmailOtp = async (email: string) => {
    await api.post('/api/auth/email/request', { email });
  };

  const verifyEmailOtp = async (email: string, code: string) => {
    const res = await api.post<AuthResult>('/api/auth/email/verify', { destination: email, code });
    tokens.save(res);
    setUser(res.user);
  };

  const loginGoogle = async (idToken: string) => {
    const res = await api.post<AuthResult>('/api/auth/google', { idToken });
    tokens.save(res);
    setUser(res.user);
  };

  const logout = () => { tokens.clear(); setUser(null); };

  return (
    <Ctx.Provider value={{ user, loading, requestEmailOtp, verifyEmailOtp, loginGoogle, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth must be used within AuthProvider');
  return c;
}
